import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Calendar,
  CalendarClock,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Copy,
  Lock,
  Pencil,
  Play,
  Plus,
  Send,
  Square,
  Trash2,
  UserCog,
} from 'lucide-react';
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Modal,
  SegmentedControl,
  Select,
  Skeleton,
  Textarea,
  toast,
} from '../../components/ui';
import { cn } from '../../lib/cn';
import { formatDate, formatMoney } from '../../lib/format';
import {
  useCreateEntry,
  useDay,
  useDeleteEntry,
  useRecentRowKeys,
  useUpdateEntry,
  useWeek,
  type DayEntry,
  type RunningTimer,
  type TimeAgreement,
} from './api';
import { weekCapacity } from './capacity';
import {
  addDaysIso,
  formatCellHours,
  formatElapsed,
  formatHoursValue,
  mondayOf,
  parseHoursInput,
  todayIso,
  weekdayLabel,
  type HoursFormat,
} from './hours';
import { EditedByMark } from './authorship';
import { rowLabel } from './labels';
import { AgreementTaskPicker } from './AgreementTaskPicker';
import { dayReview } from './timesheet-status';

// ---------------------------------------------------------------------------
// Add / edit entry modal
// ---------------------------------------------------------------------------

interface EntryModalProps {
  open: boolean;
  onClose: () => void;
  date: string;
  personId: string | null;
  agreements: TimeAgreement[];
  hoursFormat: HoursFormat;
  /** Present = edit mode. */
  entry?: DayEntry | null;
  /**
   * A NEW entry that arrives with its fields already filled — "duplicate this
   * entry", or a recent agreement/task chip. Nothing is written until Save, so a
   * mis-tap costs a keystroke rather than an hour in the billing database.
   */
  prefill?: {
    agreementId: string;
    taskId: string | null;
    hoursText?: string;
    notes?: string;
    billable?: boolean;
  } | null;
  /** Delete from inside the editor — you should not have to close it first. */
  onDelete?: (entry: DayEntry) => void;
}

/** The task a moved entry keeps: the same task, when the new agreement offers it. */
function keptTask(agreement: TimeAgreement | undefined, taskId: string): string {
  if (!agreement || !taskId) return '';
  return (agreement.tasks ?? []).some((t) => t.id === taskId) ? taskId : '';
}

function EntryModal({
  open,
  onClose,
  date,
  personId,
  agreements,
  hoursFormat,
  entry,
  prefill,
  onDelete,
}: EntryModalProps) {
  const [agreementId, setAgreementId] = useState('');
  const [taskId, setTaskId] = useState('');
  const [picking, setPicking] = useState(false);
  /** Edit mode: the agreement picker is open to MOVE the entry elsewhere. */
  const [moving, setMoving] = useState(false);
  const [hoursText, setHoursText] = useState('');
  const [entryDate, setEntryDate] = useState(date);
  const [notes, setNotes] = useState('');
  const [billable, setBillable] = useState(true);
  /**
   * Edit mode: the billable flag as it stood the moment a move target was
   * picked (null = no move pending). Picking a target applies the new
   * agreement's default; backing out of the move must put back exactly this,
   * or "Undo move" quietly un-bills (or bills) the entry on the agreement it
   * never left — `billable` is always sent on Save.
   */
  const [preMoveBillable, setPreMoveBillable] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  const createEntry = useCreateEntry();
  const updateEntry = useUpdateEntry();
  const saving = createEntry.isPending || updateEntry.isPending;

  useEffect(() => {
    if (open) {
      setAgreementId(entry?.agreement.id ?? prefill?.agreementId ?? '');
      setTaskId(entry?.taskId ?? prefill?.taskId ?? '');
      // The agreement/task cascade opens only when there is nothing to open it
      // for: a duplicate already knows what it is.
      setPicking(!entry && !prefill);
      setMoving(false);
      setHoursText(
        entry ? formatCellHours(entry.hours, hoursFormat) : (prefill?.hoursText ?? ''),
      );
      setEntryDate(entry?.date ?? date);
      setNotes(entry?.notes ?? prefill?.notes ?? '');
      setBillable(entry ? entry.billable : (prefill?.billable ?? true));
      setPreMoveBillable(null);
      setError(null);
    }
  }, [open, entry, prefill, hoursFormat, date]);

  // The picker list (with tasks) is the source of truth for the cascade —
  // entry.agreement on edits carries no task list.
  const activeAgreement = agreements.find((p) => p.id === agreementId);
  const taskOptionsSource = activeAgreement?.tasks ?? [];

  // Picking a task refines the billable default (new entries only).
  const onPickTask = (id: string) => {
    setTaskId(id);
    if (!entry) {
      const task = taskOptionsSource.find((t) => t.id === id);
      if (task) setBillable(task.billable);
    }
  };

  const submit = async () => {
    const hours = parseHoursInput(hoursText);
    if (!entry && !agreementId) {
      setError('Pick an agreement.');
      return;
    }
    if (taskOptionsSource.length > 0 && !taskId) {
      setError('Pick a task.');
      return;
    }
    if (hours === null || hours <= 0 || hours > 24) {
      setError('Enter hours as "1:30" or "1.5" (up to 24).');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(entryDate)) {
      setError('Pick a valid date.');
      return;
    }
    setError(null);
    try {
      if (entry) {
        const movedAgreement = agreementId && agreementId !== entry.agreement.id;
        await updateEntry.mutateAsync({
          id: entry.id,
          hours,
          notes: notes.trim() ? notes.trim() : null,
          billable,
          taskId: taskId || null,
          // Only sent when it actually moved, so an unrelated edit never
          // rewrites the date or the agreement.
          ...(entryDate !== entry.date ? { date: entryDate } : {}),
          ...(movedAgreement ? { agreementId } : {}),
        });
        if (movedAgreement) {
          toast.success(
            `Moved to ${activeAgreement ? rowLabel(activeAgreement, null).full : 'the other agreement'} — the bill rate follows the new agreement.`,
          );
        } else if (entryDate !== entry.date) {
          toast.success(`Moved to ${formatDate(entryDate)}.`);
        }
      } else {
        await createEntry.mutateAsync({
          personId: personId ?? undefined,
          agreementId,
          taskId: taskId || undefined,
          date: entryDate,
          hours,
          notes: notes.trim() ? notes.trim() : undefined,
          billable,
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Saving failed — try again.');
    }
  };

  const taskOptions = useMemo(
    () => taskOptionsSource.map((t) => ({ value: t.id, label: t.name })),
    [taskOptionsSource],
  );

  const chosenLabel = activeAgreement
    ? rowLabel(activeAgreement, taskOptionsSource.find((t) => t.id === taskId)?.name ?? null)
    : entry
      ? rowLabel(entry.agreement, entry.task?.name ?? null)
      : null;

  return (
    <Modal
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={entry ? 'Edit time entry' : 'New time entry'}
      description={formatDate(entryDate)}
      footer={
        <>
          {entry && onDelete && (
            <Button
              variant="ghost"
              className="mr-auto text-primary hover:bg-primary/10"
              disabled={saving}
              onClick={() => {
                onClose();
                onDelete(entry);
              }}
            >
              <Trash2 className="h-4 w-4" aria-hidden /> Delete
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} loading={saving}>
            {entry ? 'Save changes' : 'Add entry'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <p className="label">Agreement</p>
          {entry && moving ? (
            // ⚠ LOGGED ON THE WRONG PROJECT is the second most common timesheet
            // mistake after the wrong day, and re-typing the entry loses its
            // note and its history. The same cascade as a new entry; the task
            // is kept when the new agreement offers it.
            <AgreementTaskPicker
              inline
              agreements={agreements.filter((a) => a.id !== entry.agreement.id)}
              personId={personId}
              onPick={(agreement, task) => {
                setAgreementId(agreement.id);
                setTaskId(task?.id ?? keptTask(agreement, entry.taskId ?? ''));
                // The new agreement's own default — shown on the checkbox
                // below, so the reader sees (and can change) what it becomes.
                // What it was is kept, so "Undo move" can put it back.
                setPreMoveBillable(billable);
                setBillable(task ? task.billable : agreement.billBy !== 'NONE');
                setMoving(false);
              }}
              onClose={() => setMoving(false)}
              exhaustedLabel="No other agreement to move it to."
            />
          ) : entry ? (
            <div className="flex items-center gap-2 rounded-lg border border-grey-200 bg-grey-50 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-secondary">
                  {chosenLabel?.agreement}
                  {chosenLabel?.task ? ` · ${chosenLabel.task}` : ''}
                </p>
                <p className="truncate text-caption text-grey-500">{chosenLabel?.client}</p>
                {agreementId !== entry.agreement.id && (
                  <p className="mt-0.5 text-caption font-semibold text-accent">
                    Moving from {rowLabel(entry.agreement, entry.task?.name ?? null).full}
                  </p>
                )}
              </div>
              {entry.locked ? null : agreementId !== entry.agreement.id ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setAgreementId(entry.agreement.id);
                    setTaskId(entry.taskId ?? '');
                    // Undoing the move undoes EVERYTHING picking a target
                    // changed — the billable flag included.
                    setBillable(preMoveBillable ?? entry.billable);
                    setPreMoveBillable(null);
                  }}
                >
                  Undo move
                </Button>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => setMoving(true)}>
                  Move…
                </Button>
              )}
            </div>
          ) : picking ? (
            // The same searchable cascade the week grid uses — one agreement
            // picker across the whole feature, not a 30-item native select.
            <AgreementTaskPicker
              inline
              agreements={agreements}
              personId={personId}
              onPick={(agreement, task) => {
                setAgreementId(agreement.id);
                setTaskId(task?.id ?? '');
                setBillable(task ? task.billable : agreement.billBy !== 'NONE');
                setPicking(false);
              }}
              onClose={() => agreementId && setPicking(false)}
              exhaustedLabel="No agreements available."
            />
          ) : (
            <div className="flex items-center gap-2 rounded-lg border border-grey-200 bg-grey-50 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-secondary">
                  {chosenLabel?.agreement}
                  {chosenLabel?.task ? ` · ${chosenLabel.task}` : ''}
                </p>
                <p className="truncate text-caption text-grey-500">{chosenLabel?.client}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setPicking(true)}>
                Change
              </Button>
            </div>
          )}
        </div>
        {!picking && !moving && agreementId && taskOptions.length > 0 && (
          <Select
            label="Task"
            value={taskId}
            onChange={(e) => onPickTask(e.target.value)}
            options={taskOptions}
            placeholder="Pick a task…"
          />
        )}
        <div className="flex flex-wrap gap-4">
          <div>
            <label htmlFor="entry-hours" className="label">
              Hours
            </label>
            <input
              id="entry-hours"
              value={hoursText}
              onChange={(e) => setHoursText(e.target.value)}
              // Leads with the format this sheet is showing — a "1:30" hint
              // under a sheet set to 1.50 contradicts every figure around it.
              placeholder={hoursFormat === 'hm' ? '1:30 or 1.5' : '1.50 or 1:30'}
              inputMode="decimal"
              className="input num w-32 py-2.5 text-sm"
            />
          </div>
          {/* An entry logged on the wrong day is the single most common
              timesheet mistake — it has to be fixable here, not by deleting
              and retyping it (which loses the note and the bill rate). */}
          <div>
            <span className="label">Date</span>
            {/* A bare `<input type="date">` dropped the browser's own
                "08/10/2026" chrome into the middle of a custom design system.
                Same construction as the page header's date jump: our field,
                our type, our date format — with the native picker kept as an
                invisible overlay so the calendar UI is still one click away
                and the control is still a real date input for the keyboard. */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setEntryDate((d) => addDaysIso(d, -1))}
                aria-label="Previous day"
                className="flex h-[42px] w-8 shrink-0 items-center justify-center rounded-lg border border-grey-200 bg-white text-grey-500 transition-colors hover:bg-grey-50 hover:text-secondary"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
              </button>
              <label className="relative flex h-[42px] w-52 cursor-pointer items-center gap-2 rounded-lg border border-grey-200 bg-white px-3 text-sm text-secondary transition-colors hover:border-grey-300 focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/20">
                <Calendar className="h-4 w-4 shrink-0 text-grey-400" aria-hidden />
                <span className="truncate font-medium">{formatDate(entryDate)}</span>
                <input
                  id="entry-date"
                  type="date"
                  value={entryDate}
                  onChange={(e) => e.target.value && setEntryDate(e.target.value)}
                  aria-label="Entry date"
                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                />
              </label>
              <button
                type="button"
                onClick={() => setEntryDate((d) => addDaysIso(d, 1))}
                aria-label="Next day"
                className="flex h-[42px] w-8 shrink-0 items-center justify-center rounded-lg border border-grey-200 bg-white text-grey-500 transition-colors hover:bg-grey-50 hover:text-secondary"
              >
                <ChevronRight className="h-4 w-4" aria-hidden />
              </button>
            </div>
            {entry && entryDate !== entry.date && (
              <p className="mt-1 text-caption font-semibold text-accent">
                Moving from {formatDate(entry.date)}
              </p>
            )}
          </div>
        </div>
        <Textarea
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="What did you work on?"
          textareaClassName="resize-none"
        />
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-secondary">
          <input
            type="checkbox"
            checked={billable}
            onChange={(e) => setBillable(e.target.checked)}
            className="h-4 w-4 rounded border-grey-300 text-accent focus:ring-accent/30"
          />
          Billable
        </label>
        {error && (
          <p className="flex items-center gap-1.5 text-body-sm text-error">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden /> {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Week context strip
// ---------------------------------------------------------------------------

function stripDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * The seven days of the week this day belongs to, with their totals.
 *
 * A day on its own has no context: you cannot see that Tuesday is empty or that
 * the week is already at capacity. This is one click to any other day and one
 * glance at where the week stands — the same week data the grid already loaded,
 * so it is usually served straight from the cache.
 */
function WeekStrip({
  date,
  personId,
  hoursFormat,
  onSelectDay,
}: {
  date: string;
  personId: string | null;
  hoursFormat: HoursFormat;
  onSelectDay: (date: string) => void;
}) {
  const week = useWeek(mondayOf(date), personId);
  const today = todayIso();

  if (!week.data) {
    return (
      <Card className="mb-3" aria-busy>
        <div className="flex flex-row items-center gap-2 px-3 py-2.5">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-11 flex-1" />
          ))}
        </div>
      </Card>
    );
  }

  const { days, dayTotals, weekTotal, person } = week.data;
  const capacity = weekCapacity(person);

  return (
    /*
     * ⚠ The horizontal strip lives in a child `<div>`, NOT on the Card itself.
     * `Card` is `flex flex-col` by design (a card fills the height its row gives
     * it), and tailwind-merge treats `flex` (display) and `flex-col`
     * (flex-direction) as different property groups — so a caller passing
     * `className="flex …"` does NOT override the column direction. This strip
     * shipped as a 7-item VERTICAL stack ~800px tall for exactly that reason,
     * pushing every entry of the day below the fold. A row inside the card is
     * the shape the primitive is built for; do not move these classes back up.
     */
    <Card className="mb-3">
      <div className="flex flex-row items-stretch overflow-x-auto">
        {days.map((day) => {
          const total = dayTotals[day] ?? 0;
          const selected = day === date;
          return (
            <button
              key={day}
              type="button"
              onClick={() => onSelectDay(day)}
              aria-current={selected ? 'date' : undefined}
              title={`${formatDate(day)} — ${formatHoursValue(total, hoursFormat)} tracked`}
              className={cn(
                // Narrow enough that all seven days fit a 390px phone.
                'flex min-w-0 flex-1 flex-col items-center gap-0.5 border-b-2 px-0.5 py-2 transition-colors sm:min-w-[3.5rem] sm:px-1',
                selected
                  ? 'border-accent bg-accent/[0.06]'
                  : 'border-transparent hover:bg-grey-50',
              )}
            >
              <span
                className={cn(
                  'text-[10px] font-bold uppercase leading-none tracking-wide sm:text-caption sm:tracking-wider',
                  selected || day === today ? 'text-accent-700' : 'text-grey-500',
                )}
              >
                {weekdayLabel(day)}
              </span>
              <span className="hidden whitespace-nowrap text-caption text-grey-400 num sm:block">
                {stripDate(day)}
              </span>
              <span
                className={cn(
                  'text-xs font-bold num sm:text-sm',
                  total > 0 ? 'text-secondary' : 'text-grey-300',
                )}
              >
                {total > 0 ? formatHoursValue(total, hoursFormat) : '—'}
              </span>
            </button>
          );
        })}
        <div className="flex shrink-0 flex-col items-end justify-center border-l border-grey-100 px-2 py-2 sm:px-4">
          <span className="text-[10px] font-bold uppercase leading-none tracking-wide text-grey-500 sm:text-caption sm:tracking-wider">
            Week
          </span>
          <span className="whitespace-nowrap text-xs font-black text-secondary num sm:text-sm">
            {formatHoursValue(weekTotal, hoursFormat)}
            {capacity.weekly !== null && (
              <span className="font-semibold text-grey-400"> / {capacity.weeklyLabel}</span>
            )}
          </span>
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Day view
// ---------------------------------------------------------------------------

export interface DayViewProps {
  date: string;
  personId: string | null;
  agreements: TimeAgreement[];
  runningTimer: RunningTimer | null;
  timerBusy: boolean;
  hoursFormat: HoursFormat;
  onHoursFormatChange: (format: HoursFormat) => void;
  onStartTimer: (agreementId: string, taskId: string | null, notes?: string) => void;
  onStopTimer: () => void;
  /** Jump to another day from the week strip. */
  onSelectDay: (date: string) => void;
  /**
   * ⚠ This is somebody ELSE's day (an approver reviewing a colleague). Edits
   * stay possible and are recorded server-side; the one thing that is not
   * offered is STARTING a clock, because a timer measures the person at the
   * keyboard and can only ever be their own.
   */
  foreignSheet?: boolean;
  /** A financial admin may correct a SUBMITTED period (never an approved one). */
  isAdmin?: boolean;
}

export function DayView({
  date,
  personId,
  agreements,
  runningTimer,
  timerBusy,
  hoursFormat,
  onHoursFormatChange,
  onStartTimer,
  onStopTimer,
  onSelectDay,
  foreignSheet = false,
  isAdmin = false,
}: DayViewProps) {
  const day = useDay(date, personId);
  const deleteEntry = useDeleteEntry();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<DayEntry | null>(null);
  const [prefill, setPrefill] = useState<EntryModalProps['prefill']>(null);
  const [toDelete, setToDelete] = useState<DayEntry | null>(null);
  /**
   * Agreement × task combos this person actually used recently — the same
   * server-side list the week grid's "pick up where you left off" strip reads.
   * On a phone this view IS the timesheet (the week grid needs ~800px), and its
   * empty state offered one button and a three-step cascade behind it.
   */
  const recent = useRecentRowKeys(personId);
  /**
   * The chips, minus anything already on this day: offering "add Support again"
   * under a list that already has it is a chip that does nothing new.
   */
  const recentChips = useMemo(() => {
    const here = new Set(
      (day.data?.entries ?? []).map((e) => `${e.agreement.id}:${e.taskId ?? ''}`),
    );
    return [...(recent.data?.recent ?? []), ...(recent.data?.rows ?? [])]
      .filter((row) => {
        const key = `${row.agreement.id}:${row.taskId ?? ''}`;
        if (here.has(key)) return false;
        here.add(key);
        return true;
      })
      .slice(0, 5);
  }, [recent.data, day.data]);

  // Live timer: seconds tick on the running entry (and the day total) until the
  // timer stops and the elapsed hours are committed to the entry.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!runningTimer) return;
    setNow(Date.now());
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [runningTimer]);
  const runningElapsedMs = runningTimer
    ? Math.max(0, now - new Date(runningTimer.startedAt).getTime())
    : 0;
  /** Unsaved hours the running timer adds to this day (0 when it runs elsewhere). */
  const runningHours =
    runningTimer && day.data?.entries.some((e) => e.id === runningTimer.entryId)
      ? Math.round((runningElapsedMs / 3_600_000) * 100) / 100
      : 0;

  const confirmDelete = async () => {
    const entry = toDelete;
    if (!entry) return;
    try {
      await deleteEntry.mutateAsync(entry.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Deleting failed — try again.');
    }
  };

  const openEdit = (entry: DayEntry) => {
    setPrefill(null);
    setEditing(entry);
    setModalOpen(true);
  };

  /** New entry, blank. */
  const openNew = () => {
    setEditing(null);
    setPrefill(null);
    setModalOpen(true);
  };

  /**
   * Harvest has a duplicate on every row and this view had none: repeating
   * yesterday's three lines meant three trips through the agreement → task →
   * hours cascade. The copy arrives filled in — hours and note included — and
   * saves in one more click.
   */
  const duplicate = (entry: DayEntry) => {
    setEditing(null);
    setPrefill({
      agreementId: entry.agreement.id,
      taskId: entry.taskId,
      hoursText: formatCellHours(entry.hours, hoursFormat),
      notes: entry.notes ?? undefined,
      billable: entry.billable,
    });
    setModalOpen(true);
  };

  /** A recent row chip: the agreement and task are known, the hours are not. */
  const startFromRecent = (agreementId: string, taskId: string | null) => {
    setEditing(null);
    setPrefill({ agreementId, taskId });
    setModalOpen(true);
  };

  const strip = (
    <WeekStrip
      date={date}
      personId={personId}
      hoursFormat={hoursFormat}
      onSelectDay={onSelectDay}
    />
  );

  if (day.isPending) {
    return (
      <>
        {strip}
        <Card className="p-6">
          <div className="space-y-3" aria-busy>
            <Skeleton className="h-4 w-40" />
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        </Card>
      </>
    );
  }

  if (day.isError) {
    return (
      <>
        {strip}
        <ErrorState
          card
          title="Couldn't load this day"
          message={day.error instanceof Error ? day.error.message : 'Something went wrong.'}
          onRetry={() => day.refetch()}
        />
      </>
    );
  }

  const entries = day.data.entries;
  /**
   * The REVIEW lock of the WEEK this day belongs to.
   *
   * A day view that still offered "New entry" on a submitted week would send
   * the user through a whole modal to reach a 409 — the lock has to be visible
   * before the click, not after it.
   */
  // The day's own period decides (a month, a week or the day itself); the
  // status strip above this view names it and carries its Submit. An admin
  // may still correct a submitted period — the server allows it.
  const review = dayReview(day.data, date, isAdmin);
  const reviewLocked = review.lock;
  const sheet = review;

  return (
    <>
      {strip}
      <Card>
        {entries.length === 0 ? (
          <>
            <EmptyState
              icon={<CalendarClock aria-hidden />}
              title="No time on this day"
              description={
                reviewLocked
                  ? 'Nothing was logged here, and this period is closed for edits.'
                  : 'Track your first entry — or start a timer and let it run while you work.'
              }
              action={
                reviewLocked ? undefined : (
                  <Button onClick={openNew}>
                    <Plus className="h-4 w-4" aria-hidden /> New entry
                  </Button>
                )
              }
            />
            {/* One tap to the work you were on yesterday, instead of a blank
                cascade — the week grid has had this strip since it shipped and
                the day view, which is what a phone opens on, had nothing. */}
            {!reviewLocked && recentChips.length > 0 && (
              <div className="border-t border-grey-100 px-4 py-3 sm:px-6">
                <p className="mb-2 text-caption font-semibold uppercase tracking-wide text-grey-500">
                  Pick up where you left off
                </p>
                <div className="flex flex-wrap gap-2">
                  {recentChips.map((row) => {
                    const label = rowLabel(row.agreement, row.task?.name ?? null);
                    return (
                      <button
                        key={`${row.agreement.id}:${row.taskId ?? ''}`}
                        type="button"
                        onClick={() => startFromRecent(row.agreement.id, row.taskId)}
                        title={`Log time on ${label.full}`}
                        className="flex items-center gap-1.5 rounded-lg border border-grey-200 bg-white px-2.5 py-1.5 text-left text-caption transition-colors hover:border-accent/50 hover:bg-accent/5"
                      >
                        <Plus className="h-3 w-3 shrink-0 text-grey-400" aria-hidden />
                        <span className="min-w-0">
                          <span className="block truncate font-semibold text-secondary">
                            {label.agreement}
                            {label.task && (
                              <span className="ml-1.5 font-normal text-grey-500">{label.task}</span>
                            )}
                          </span>
                          <span className="block truncate text-grey-500">{label.client}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-end gap-3 border-t border-grey-200 bg-grey-50 px-4 py-3 sm:px-6">
              <SegmentedControl
                ariaLabel="Hours format"
                value={hoursFormat}
                onChange={onHoursFormatChange}
                options={[
                  { value: 'decimal', label: <span className="num">1.50</span> },
                  { value: 'hm', label: <span className="num">1:30</span> },
                ]}
              />
              <p className="text-sm text-grey-500">
                Day total{' '}
                <span className="ml-2 text-base font-black text-secondary num">
                  {formatHoursValue(0, hoursFormat)}
                </span>
              </p>
            </div>
          </>
        ) : (
          <>
            <ul className="divide-y divide-grey-100">
              {entries.map((entry) => {
                const timerHere = runningTimer?.entryId === entry.id;
                const label = rowLabel(entry.agreement, entry.task?.name ?? null);
                // Gone from Harvest: kept as history (it may be invoiced), but
                // it is not editable work and it can never be billed.
                const gone = entry.deletedInSource;
                // Three independent reasons a row cannot be touched: billed,
                // deleted in Harvest, or its week is under review. The banner
                // above names the third; the row only needs to stop offering
                // edits it cannot deliver.
                const readOnly = entry.locked || gone || reviewLocked !== null;
                return (
                  <li
                    key={entry.id}
                    // The whole row is the hit target. This is a list of five
                    // records you came here to change; making the only way in a
                    // 16px pencil that appears on hover is a mouse-accuracy
                    // test. The buttons inside keep their own click — the
                    // `closest('button')` check hands them back.
                    onClick={(event) => {
                      if (readOnly) return;
                      if ((event.target as HTMLElement).closest('button')) return;
                      openEdit(entry);
                    }}
                    title={
                      gone
                        ? 'Deleted in Harvest — kept here as history, not editable'
                        : reviewLocked
                          ? reviewLocked
                          : entry.locked
                            ? undefined
                            : 'Click to edit this entry'
                    }
                    className={cn(
                      'group flex items-center gap-2 px-3 py-2.5 transition-colors sm:gap-3 sm:px-6',
                      !readOnly && 'cursor-pointer hover:bg-grey-50',
                      gone && 'bg-grey-50/60',
                    )}
                  >
                    <button
                      type="button"
                      disabled={timerBusy || readOnly || (foreignSheet && !timerHere)}
                      onClick={() =>
                        timerHere
                          ? onStopTimer()
                          : onStartTimer(entry.agreement.id, entry.taskId, entry.notes ?? undefined)
                      }
                      title={
                        timerHere
                          ? 'Stop timer'
                          : foreignSheet
                            ? `${day.data?.person.name ?? 'This person'} starts their own timer — this is their timesheet, not yours.`
                            : 'Continue this work (starts a timer today)'
                      }
                      className={cn(
                        'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-all sm:h-8 sm:w-8',
                        timerHere
                          ? 'border-primary bg-primary text-white hover:bg-primary-600'
                          : readOnly
                            ? 'cursor-not-allowed border-grey-100 text-grey-200'
                            : 'border-grey-200 bg-white text-grey-400 hover:border-primary/40 hover:bg-primary/5 hover:text-primary',
                      )}
                    >
                      {timerHere ? (
                        <Square className="h-3 w-3 fill-current" aria-hidden />
                      ) : (
                        <Play className="h-3 w-3 translate-x-px fill-current" aria-hidden />
                      )}
                      <span className="sr-only">{timerHere ? 'Stop timer' : 'Start timer'}</span>
                    </button>

                    {/* The note is the billing narrative — it leads the row. */}
                    <div className="min-w-0 flex-1">
                      {entry.notes ? (
                        <p
                          className={cn(
                            'truncate text-sm font-semibold text-secondary',
                            gone && 'text-grey-400 line-through decoration-grey-400',
                          )}
                          title={entry.notes}
                        >
                          {entry.notes}
                        </p>
                      ) : readOnly ? (
                        <p className="text-sm italic text-grey-400">No note</p>
                      ) : (
                        <button
                          type="button"
                          onClick={() => openEdit(entry)}
                          className="text-sm italic text-grey-400 underline-offset-2 hover:text-accent hover:underline"
                        >
                          Add a note
                        </button>
                      )}
                      {/* Wraps rather than crushing any one token: on a phone the
                          agreement, the task and the client must all stay legible. */}
                      <p className="flex flex-wrap items-center gap-x-1.5 text-caption text-grey-500">
                        <span className="min-w-0 max-w-full truncate font-semibold text-grey-600">
                          {label.agreement}
                        </span>
                        {label.task && (
                          <span className="max-w-full shrink-0 truncate rounded bg-grey-100 px-1.5 py-px text-[11px] font-semibold text-grey-600">
                            {label.task}
                          </span>
                        )}
                        {/* Separator travels with the client so a wrapped line
                            never ends on a dangling "·". */}
                        <span className="flex min-w-0 max-w-full items-center gap-1.5">
                          <span aria-hidden>·</span>
                          <span className="min-w-0 truncate">{label.client}</span>
                        </span>
                        {entry.billable && (
                          <span
                            className="h-1.5 w-1.5 shrink-0 rounded-full bg-success"
                            title="Billable"
                            aria-hidden
                          />
                        )}
                        {/* ⚠ SOMEBODY ELSE WROTE THIS HOUR. The day view is
                            where one entry is read in full, so the author
                            belongs on the row itself and not only in the
                            week's notice. */}
                        {entry.editedBy && <EditedByMark editedBy={entry.editedBy} />}
                      </p>
                    </div>

                    {/* What this line is WORTH — the fact that makes this a
                        financials app rather than a stopwatch, and the one
                        thing the wide empty middle of a desktop row should be
                        carrying. Only for hourly-billed billable work: an
                        hourly valuation of fixed-fee or internal time is not
                        billable and must never be printed as if it were. */}
                    <span
                      className="hidden w-28 shrink-0 text-right text-caption num md:block"
                      title={
                        gone
                          ? 'Deleted in Harvest — this work can never be billed, whatever the rate says.'
                          : entry.billable && entry.billRate && entry.agreement.billBy === 'HOURS'
                            ? `${formatHoursValue(entry.hours, hoursFormat)} × ${formatMoney(
                                entry.billRate,
                              )}/h — billable value of this entry`
                            : entry.billable
                              ? 'Billable, but this agreement is not billed by the hour — its value comes from the fee, not these hours.'
                              : 'Internal time — not billable'
                      }
                    >
                      {entry.billable && entry.billRate && entry.agreement.billBy === 'HOURS' ? (
                        // Struck through when the entry is gone: this number is
                        // not money anybody may invoice, and printing it plain
                        // next to "Deleted in Harvest" would say it is.
                        <span
                          className={cn(
                            gone ? 'text-grey-300 line-through decoration-grey-300' : 'text-grey-500',
                          )}
                        >
                          {formatMoney(entry.hours * entry.billRate)}
                        </span>
                      ) : (
                        <span className="text-grey-300">{entry.billable ? 'Fee work' : 'Internal'}</span>
                      )}
                    </span>

                    {timerHere ? (
                      <span
                        className="flex items-center gap-1.5 text-base font-bold text-primary num"
                        title={`Timer running — ${formatElapsed(runningElapsedMs)} so far`}
                      >
                        <span className="relative flex h-1.5 w-1.5" aria-hidden>
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
                        </span>
                        {formatElapsed(entry.hours * 3_600_000 + runningElapsedMs)}
                      </span>
                    ) : (
                      <span
                        className={cn(
                          'shrink-0 text-sm font-semibold num sm:text-base',
                          gone ? 'text-grey-400 line-through decoration-grey-400' : 'text-secondary',
                        )}
                      >
                        {formatHoursValue(entry.hours, hoursFormat)}
                      </span>
                    )}

                    {gone ? (
                      <span
                        className="flex items-center gap-1 rounded-full bg-warning/15 px-2.5 py-1 text-caption font-semibold text-status-partial"
                        title={
                          'This entry was deleted in Harvest' +
                          (entry.deletedInSourceAt
                            ? ` on ${formatDate(entry.deletedInSourceAt)}`
                            : '') +
                          '. It is kept here because it may already be invoiced, cannot be edited, and is never offered for billing. Review it in Settings → Harvest sync.'
                        }
                      >
                        <AlertTriangle className="h-3 w-3" aria-hidden /> Deleted in Harvest
                      </span>
                    ) : entry.locked ? (
                      // BILLED outranks the week's review state on the row:
                      // it is row-specific, permanent until the invoice is
                      // voided, and the banner above already says the week is
                      // under review. Losing "Billed" to an "Approved" chip
                      // would hide the fact that matters to money.
                      <span
                        className="flex items-center gap-1 rounded-full bg-grey-100 px-2.5 py-1 text-caption font-semibold text-grey-500"
                        title={`Billed on invoice ${entry.invoiceId} — locked`}
                      >
                        <Lock className="h-3 w-3" aria-hidden /> Billed
                      </span>
                    ) : reviewLocked ? (
                      // The REVIEW lock, said in its own words. Falling through
                      // to the edit/delete pair here left a pencil on an
                      // approved week that only ever produced a 409 — and
                      // reusing the "Billed" chip would blame the invoice for a
                      // lock the invoice had nothing to do with.
                      <span
                        className="flex items-center gap-1 rounded-full bg-grey-100 px-2.5 py-1 text-caption font-semibold text-grey-500"
                        title={reviewLocked}
                      >
                        {/* ⚠ NOT the padlock: `Lock` is the BILLING lock (the
                            chip directly above), which the invoice owns and
                            which this screen cannot undo. The review lock wears
                            the week's own status glyph, the same one on the
                            pill at the top of the page. */}
                        {sheet.status === 'APPROVED' ? (
                          <CheckCircle2 className="h-3 w-3" aria-hidden />
                        ) : (
                          <Send className="h-3 w-3" aria-hidden />
                        )}
                        {sheet.status === 'APPROVED' ? 'Approved' : 'Submitted'}
                      </span>
                    ) : (
                      // Always visible, quiet until you reach for them. Hiding
                      // edit/delete behind hover made a desktop screenshot of
                      // this list read as "these rows cannot be changed" — the
                      // affordance has to be discoverable without a pointer
                      // already sitting on the row.
                      <span className="flex items-center gap-0.5 opacity-60 transition-opacity focus-within:opacity-100 group-hover:opacity-100 max-md:opacity-100">
                        <button
                          type="button"
                          onClick={() => openEdit(entry)}
                          title="Edit entry"
                          className="rounded-md p-1 text-grey-400 transition-colors hover:bg-grey-100 hover:text-secondary sm:p-1.5"
                        >
                          <Pencil className="h-4 w-4" aria-hidden />
                          <span className="sr-only">Edit entry</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => duplicate(entry)}
                          title="Duplicate this entry — same agreement, task, note and hours"
                          className="rounded-md p-1 text-grey-400 transition-colors hover:bg-grey-100 hover:text-secondary sm:p-1.5"
                        >
                          <Copy className="h-4 w-4" aria-hidden />
                          <span className="sr-only">Duplicate entry</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setToDelete(entry)}
                          title="Delete entry"
                          className="rounded-md p-1 text-grey-400 transition-colors hover:bg-grey-100 hover:text-primary sm:p-1.5"
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                          <span className="sr-only">Delete entry</span>
                        </button>
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-grey-200 bg-grey-50 px-4 py-3 sm:px-6">
              {reviewLocked ? (
                <p className="flex items-center gap-1.5 text-caption font-semibold text-grey-500">
                  {sheet.status === 'APPROVED' ? (
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  ) : (
                    <Send className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  )}
                  {sheet.status === 'APPROVED'
                    ? 'Approved — read-only until it is reopened'
                    : 'Submitted — read-only until it is withdrawn'}
                </p>
              ) : (
                <Button variant="ghost" size="sm" onClick={openNew}>
                  <Plus className="h-4 w-4" aria-hidden /> New entry
                </Button>
              )}
              {review.correcting && (
                <p className="flex items-center gap-1.5 text-caption font-semibold text-accent-700">
                  <UserCog className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  Correcting a submitted period as an admin
                </p>
              )}
              <div className="ml-auto flex items-center gap-3">
                {/* Display preference sits with the numbers it formats. */}
                <SegmentedControl
                  ariaLabel="Hours format"
                  value={hoursFormat}
                  onChange={onHoursFormatChange}
                  options={[
                    { value: 'decimal', label: <span className="num">1.50</span> },
                    { value: 'hm', label: <span className="num">1:30</span> },
                  ]}
                />
                <p className="text-sm text-grey-500">
                  Day total{' '}
                  <span className="ml-2 text-base font-black text-secondary num">
                    {formatHoursValue(day.data.total + runningHours, hoursFormat)}
                  </span>
                </p>
              </div>
            </div>
          </>
        )}
      </Card>

      <EntryModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
          setPrefill(null);
        }}
        date={date}
        personId={personId}
        agreements={agreements}
        hoursFormat={hoursFormat}
        entry={editing}
        prefill={prefill}
        onDelete={setToDelete}
      />

      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => !open && setToDelete(null)}
        title="Delete this entry?"
        destructive
        confirmLabel="Delete entry"
        description={
          toDelete
            ? `${formatHoursValue(toDelete.hours, hoursFormat)} hours${
                toDelete.notes ? ` — “${toDelete.notes}”` : ''
              } on ${rowLabel(toDelete.agreement, toDelete.task?.name ?? null).full} will be removed.`
            : undefined
        }
        onConfirm={confirmDelete}
      />
    </>
  );
}
